// Tier-2 <Table> backed by AntD Table (FORT-331). A generic, data-driven table:
// `columns` describe how to render each field, `dataSource` is the row data.
// Rendered inside a wrapper div that carries className/style/data-testid (AntD
// Table doesn't reliably forward arbitrary DOM attrs). Colours come from the
// token-sourced theme, so no useTokens() here.
import React from 'react';
import { Table as AntTable } from 'antd';
import type { TableProps as AntTableProps } from 'antd';
import type { CommonProps } from './types';

/** One column definition (AntD's column shape, re-exported under our vocabulary). */
export type TableColumn<T> = NonNullable<AntTableProps<T>['columns']>[number];

export interface TableProps<T> extends CommonProps {
  columns: TableColumn<T>[];
  dataSource: readonly T[];
  rowKey?: AntTableProps<T>['rowKey'];
  loading?: boolean;
  bordered?: boolean;
  size?: 'small' | 'middle' | 'large';
  /** `false` disables paging; otherwise AntD's pagination config. */
  pagination?: AntTableProps<T>['pagination'];
  scroll?: AntTableProps<T>['scroll'];
  onRow?: AntTableProps<T>['onRow'];
  /**
   * Render only the rows in view instead of the whole dataset. Use for long unpaginated
   * lists — without it every row lands in the DOM and interaction degrades badly past a
   * few hundred rows.
   *
   * `scroll.x` must be a number (AntD warns otherwise); set `scroll.y` explicitly or it
   * defaults to 500. Give every column an explicit `width` — width-less columns just take
   * an even share of the remaining `scroll.x`, which rarely matches the content.
   *
   * Tradeoffs to weigh before reaching for this: body rows render as `div`s, so they lose
   * table semantics for screen readers, and browser find-in-page / text selection / printing
   * only see the visible window. Pair it with an in-app filter or export. Note also that
   * `rowKey` is called WITHOUT an index in virtual mode, so the key must be intrinsically
   * unique — an index-derived key silently becomes `undefined`.
   */
  virtual?: boolean;
  /** Content shown when dataSource is empty. */
  emptyText?: React.ReactNode;
}

export function Table<T extends object>({
  columns,
  dataSource,
  rowKey,
  loading,
  bordered,
  size = 'middle',
  pagination,
  scroll,
  onRow,
  virtual,
  emptyText,
  className,
  style,
  'data-testid': dataTestId,
}: TableProps<T>) {
  // The copy exists only to satisfy AntD's mutable dataSource signature against our
  // `readonly T[]`. Memoise it — a fresh array identity each render re-flattens the whole
  // dataset inside rc-table, which defeats memoisation on long (virtualised) lists.
  const rows = React.useMemo(() => [...dataSource], [dataSource]);

  return (
    <div className={className} style={style} data-testid={dataTestId}>
      <AntTable<T>
        columns={columns}
        dataSource={rows}
        rowKey={rowKey}
        loading={loading}
        bordered={bordered}
        size={size}
        pagination={pagination}
        scroll={scroll}
        onRow={onRow}
        virtual={virtual}
        locale={emptyText ? { emptyText } : undefined}
      />
    </div>
  );
}
